import React, { useEffect } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Toolbar,
  Typography,
  Breadcrumbs,
  IconButton,
  Tooltip,
  Link,
} from "@mui/material";
import { getProject } from "../helpers/getProject";
import { createFolder } from "../helpers/createFolder";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  ref,
  listAll,
  getDownloadURL,
  getStorage,
  getMetadata,
} from "firebase/storage";
import FilesList from "./FilesList";
import EditFiles from "../Dialogs/EditFiles";
import NewFolderDialog from "./NewFolderDialog";
import { uploadToStorage } from "../helpers/uploadToStorage";
import { fileToBase64 } from "../helpers/fileToBase64";
import { updateStorageInFirestore } from "../helpers/updateStorageInFirestore";
import { deleteFromStorage } from "../helpers/deleteFromStorage";
import { useNavigate } from "react-router-dom";
import { getUserAccess } from "../helpers/getUserAccess";

export default function Files(props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [folders, setFolders] = React.useState([]);
  const [project, setProject] = React.useState({});
  const [newFolderName, setNewFolderName] = React.useState("");
  const [folderPath, setFolderPath] = React.useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = window.location.href.split("/");
        const projId = params[params.length - 2];

        const proj = await getProject(projId);
        setProject(proj);

        const rootPath = `users/${proj.uid}/projects/${projId}`;
        setFolderPath(rootPath);
        await listAllItemsInFolder(rootPath, projId);
        setIsLoading(false);
      } catch (error) {
        console.error("Error getting data:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
        setIsLoading(false);
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const onDeleteFile = async (file) => {
    try {
      setIsLoading(true);
      deleteFromStorage(file.fullPath);
      await updateStorageInFirestore(props.user.uid, -file.size);
      await listAllItemsInFolder(folderPath, project.id);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = async (fullPath) => {
    try {
      setIsLoading(true);
      if (fullPath !== folderPath) {
        await listAllItemsInFolder(fullPath, project.id);
        setFolderPath(fullPath);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const listAllItemsInFolder = async (folderPath, project_id) => {
    const storage = getStorage();
    const listRef = ref(storage, folderPath);
    const res = await listAll(listRef, { maxResults: 1000 });

    const folders = res.prefixes
      .filter(
        (folderRef) =>
          folderRef.fullPath.split(project_id)[1] !== "/attachments",
      )
      .map((folderRef) => ({
        name: folderRef.name,
        fullPath: folderRef.fullPath,
        type: "folder",
      }));

    const fls = await Promise.all(
      res.items
        .filter((item) => item.name !== ".ghostfile")
        .map(async (itemRef) => ({
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url: await getDownloadURL(itemRef),
          type: "file",
          size: await getMetadata(itemRef).then((metadata) => metadata.size),
          // You might want to fetch download URLs here if needed, but it adds extra calls.
        })),
    );

    setFolders(folders);
    setFiles(fls);
  };

  const getAcceptedFiles = async (newFiles) => {
    try {
      setIsLoading(true);
      var totalSize = 0;

      for (var i = 0; i < newFiles.length; i++) {
        if (newFiles[i].size > 50000000) {
          enqueueSnackbar(
            `Sorry, this file is over 50 megabytes: ${newFiles[i].name} - File is to large.`,
            {
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              variant: "error",
            },
          );
        } else if (
          files
            .map((f) => f.name.toLowerCase())
            .includes(newFiles[i].name.toLowerCase())
        ) {
          enqueueSnackbar(
            `Sorry, this file (${newFiles[i].name}) already exists in this directory.`,
            {
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              variant: "error",
            },
          );
        } else {
          const bse64 = await fileToBase64(newFiles[i]);
          await uploadToStorage(folderPath + "/" + newFiles[i].name, bse64);
          totalSize += newFiles[i].size;
        }
      }

      if (totalSize > 0) {
        await updateStorageInFirestore(props.user.uid, totalSize);
      }
      await listAllItemsInFolder(folderPath, project.id);
    } catch (error) {
      console.error("Error getting document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onAddBackFolder = async () => {
    try {
      setIsLoading(true);
      const pathParts = folderPath.split("/").filter((part) => part !== "");
      if (pathParts.length <= 3) {
        // Already at root level
      } else {
        const parentPath = pathParts.slice(0, -1).join("/");
        await listAllItemsInFolder(parentPath, project.id);
        setFolderPath(parentPath);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onAddFolder = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const onCreateFolderSave = async () => {
    try {
      setIsLoading(true);

      if (
        newFolderName.toLowerCase() === "attachments" &&
        folderPath === `users/${props.user.uid}/projects/${project.id}/`
      ) {
        throw new Error(
          'Sorry, "attachments" is a reserved folder name within the root directory of the project.',
        );
      }

      await createFolder(`${folderPath}/${newFolderName}/`);
      await listAllItemsInFolder(folderPath, project.id);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    } finally {
      setOpen(false);
      setNewFolderName("");
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div style={{ width: "100%", height: "100%" }}>
      <Scrollbars
        autoHide
        autoHideTimeout={10}
        style={{ width: "100%", height: "100%" }}
      >
        <Toolbar />
        <Container
          maxWidth="xl"
          style={{
            // paddingTop: 30,
            paddingBottom: 30,
            // paddingBottom: theme.spacing(4),
            justifyContent: "center",
          }}
        >
          <Typography
            color="primary.light"
            style={{ paddingTop: 40 }}
            variant="h5"
            gutterBottom
          >
            Project Files
          </Typography>

          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
            spacing={2}
          >
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                underline="hover"
                key="1"
                color="inherit"
                href=""
                onClick={() =>
                  navigate(`/project/${project.id}/dashboard`, {
                    state: { passProject: project },
                  })
                }
              >
                {`Project: (${project.Name})`}
              </Link>
              ,
              <Typography
                underline="hover"
                key="2"
                color="inherit"
                // href={`/project/${checklist.id}/issues`}

                // onClick={handleClick}
              >
                {`Files`}
              </Typography>
              ,
            </Breadcrumbs>
          </Stack>

          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
            spacing={2}
          >
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              {folderPath.split("/").map((part, index) =>
                index >= 3 ? (
                  <Link
                    key={index}
                    color="secondary.light"
                    variant="h6"
                    onClick={(e) => {
                      e.preventDefault();
                      const pathParts = folderPath.split("/");
                      const newPath = pathParts.slice(0, index + 1).join("/");
                      onChange(newPath);
                    }}
                  >
                    {part === project.id ? "Project Root" : part}
                  </Link>
                ) : null,
              )}
            </Breadcrumbs>
          </Stack>

          <Stack
            // style={{ paddingTop: 10 }}
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            {folderPath.split(project.id)[1] === "" ? null : (
              <Tooltip title="Previous Folder">
                <IconButton
                  disabled={
                    folderPath.split(project.id)[1] === "" ? true : false
                  }
                  onClick={onAddBackFolder}
                  aria-label="Previous Folder"
                  size="large"
                  color="primary"
                >
                  <ArrowBackIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Add Folder">
              <IconButton
                onClick={onAddFolder}
                disabled={
                  !getUserAccess(props.user, project.uid, project.Contacts)
                    .allowCreate
                }
                aria-label="Add Folder"
                size="large"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>

          <FilesList
            style={{ paddingTop: 10 }}
            files={files}
            folders={folders}
            allowDelete={
              getUserAccess(props.user, project.uid, project.Contacts)
                .allowDelete
            }
            onChange={onChange}
            onDeleteFile={onDeleteFile}
          />

          {files.length === 0 && folders.length === 0 && (
            <Typography
              color="textSecondary"
              style={{ paddingTop: 20, paddingBottom: 20 }}
              variant="body1"
              gutterBottom
            >
              No files or folders found in this directory.
            </Typography>
          )}

          {getUserAccess(props.user, project.uid, project.Contacts)
            .allowCreate && (
            <EditFiles editFiles={[]} getAcceptedFiles={getAcceptedFiles} />
          )}

          <NewFolderDialog
            open={open}
            newFolderName={newFolderName}
            onClose={onClose}
            onTextChange={(e) =>
              //this removes bad characters from folder name
              setNewFolderName(
                e.target.value.replace(/[^a-zA-Z0-9\s-_]/g, "").slice(0, 30),
              )
            }
            onCreateFolderSave={onCreateFolderSave}
          />
        </Container>
      </Scrollbars>
    </div>
  );
}
